import { parseCalendarDate } from "../../utils/date";
import { matchPresentationPhase } from "./matchPresentation";
import { hasRegistrationPolicy, registrationGate } from "./registrationPolicy";

export const textValue = (value) => typeof value === "string" ? value.trim() : "";
export const countValue = (value) => (typeof value === "number" || (typeof value === "string" && value.trim() !== ""))
  && Number.isInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;

function validDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function isUpcomingDetail(match, hasScores = false, now = new Date()) {
  return Boolean(match && !hasScores && parseCalendarDate(match.startDate || match.matchDate)
    && matchPresentationPhase(match, now) === "scheduled");
}

export function safeWebLink(value) {
  try { const url = new URL(value); return ["https:", "http:"].includes(url.protocol) ? url.href : null; }
  catch { return null; }
}

// Only the explicitly published snapshot is public-facing. Future director drafts belong elsewhere.
export function publishedCompetitorInfo(match) {
  const info = match?.competitorInfo?.publicationState === "published" ? match.competitorInfo : {};
  return {
    description: textValue(info.description) || textValue(match?.description),
    timeZone: textValue(match?.timeZone) || textValue(info.timeZone) || "UTC",
    scheduleReviewRequired: Boolean(match?.scheduleReviewRequired),
    schedule: (!match?.scheduleReviewRequired && Array.isArray(info.schedule) ? info.schedule : []).filter((item) => textValue(item?.title)
      && validDay(item.date) && /^([01]\d|2[0-3]):[0-5]\d$/.test(item.time || "")),
    checklist: (Array.isArray(info.arrivalChecklist) ? info.arrivalChecklist : []).filter((item) => textValue(item?.title)),
  };
}

export function upcomingRegistration(summary, match, registration, family = "arc") {
  const squads = family === "bullseye" ? [] : Array.isArray(summary?.squads) && summary.squads.length ? summary.squads : Array.isArray(match?.squads) ? match.squads : [];
  const registered = summary ? countValue(summary.activeRegistrationCount)
    ?? (countValue(summary.pending) !== null && countValue(summary.confirmed) !== null ? Number(summary.pending) + Number(summary.confirmed) : null)
    ?? countValue(summary.activeParticipants) : null;
  const capacities = squads.map((squad) => countValue(squad.maxCompetitorCount));
  const capacity = summary && capacities.length && capacities.every((value) => value !== null)
    ? capacities.reduce((sum, value) => sum + value, 0) : null;
  const remaining = capacity !== null && registered !== null ? Math.max(0, capacity - registered) : null;
  const active = ["pending", "confirmed", "waitlisted", "checked_in"].includes(String(registration?.status || "").toLowerCase());
  if (hasRegistrationPolicy(summary) || (summary?.registrationSettings?.effectiveWindow && summary?.gate)) {
    const gate = registrationGate(summary, match);
    const phase = matchPresentationPhase(match || {});
    const capacity = gate.capacity ?? null, remaining = gate.remaining ?? null;
    return { squads, registered, capacity, remaining, active, open: gate.canRegister, full: gate.state === "full" || remaining === 0,
      ended: phase === "completed", cancelled: phase === "cancelled", upcoming: phase === "scheduled", gate,
      label: active ? "View my entry" : gate.canRegister ? gate.approvalMode === "manual" ? "Request entry" : "Register for match" : "Registration details",
      state: ({ open: "Registration open", full: "Match full", not_open: "Registration not open yet", scheduled: "Registration not open yet", paused: "Registration paused", closed: "Registration closed", started: "Match in progress", completed: "Match ended", cancelled: "Match cancelled", program_incomplete: "Registration opens soon" })[gate.state] || "Registration details" };
  }
  const phase = matchPresentationPhase(match || {});
  const ended = phase === "completed";
  const cancelled = phase === "cancelled";
  const open = summary?.registrationOpen === true && phase === "scheduled";
  const full = remaining === 0;
  return { squads, registered, capacity, remaining, active, open, full, ended, cancelled, upcoming: phase === "scheduled",
    label: active ? "Manage registration" : open && !full ? "Register for match" : "Registration details",
    state: ended ? "Match ended" : cancelled ? "Match cancelled" : !summary ? "Registration unavailable" : !open ? "Registration closed" : full ? "Match full" : "Registration open" };
}

export function courseGroups(match, setup, family) {
  if (family === "bullseye") return (Array.isArray(setup?.gunGroups) ? setup.gunGroups : []).map((group) => ({
    ...group, stages: Array.isArray(group.stages) ? group.stages : [],
  }));
  const stages = Array.isArray(setup?.stages) && setup.stages.length ? setup.stages : Array.isArray(match?.stages) ? match.stages : [];
  return stages.length ? [{ title: "Course of fire", stages }] : [];
}

export function stringRounds(entry) {
  const direct = countValue(entry.shots);
  if (direct !== null) return direct;
  const plans = Array.isArray(entry.shotPlans) ? entry.shotPlans : [];
  return plans.length && plans.every((plan) => countValue(plan.shots) !== null)
    ? plans.reduce((sum, plan) => sum + Number(plan.shots), 0) : null;
}

export function roundCount(stages) {
  const strings = stages.flatMap((stage) => Array.isArray(stage.strings) ? stage.strings : []);
  if (!stages.length || stages.some((stage) => !stage.strings?.length) || strings.some((entry) => stringRounds(entry) === null)) return null;
  return strings.reduce((sum, entry) => sum + stringRounds(entry), 0);
}

export function localClock(value) {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value || "")) return "";
  const [hour, minute] = value.split(":").map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
}

export function squadTime(value, timeZone) {
  const date = parseCalendarDate(value);
  if (!date) return "Time to be announced";
  try { return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short", ...(timeZone ? { timeZone } : {}) }); }
  catch { return "Time to be confirmed"; }
}
